import { Box, Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { IconAlertCircle, IconGripVertical } from '@tabler/icons-react'
import type { CSSProperties, HTMLAttributes } from 'react'
import { useEffect, useRef, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import type { GetSponsorTopicsResponse } from '@/services/api/api.types'

import { EntryMenuButton } from './EntryMenuButton'

const INPUT_STYLES = {
  border: 'none',
  background: 'transparent',
  fontSize: 'inherit',
  width: '100%',
  padding: '2px 4px',
}

const TOOLTIP_STYLES = {
  tooltip: {
    maxWidth: '250px',
  },
}

const WARNING_TOOLTIP_PROPS = {
  withArrow: true,
  arrowSize: 12,
  position: 'top' as const,
  offset: 8,
  multiline: true,
  styles: TOOLTIP_STYLES,
  label: 'This topic is associated with one or more modules, changes may impact the experience of users in the app.',
}

interface TopicEntryMenuProps {
  onDuplicate?: () => void
  onDelete?: () => void
  canDelete?: boolean
  canDuplicate?: boolean
}

interface TopicEntryDragHandleProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  attributes?: HTMLAttributes<HTMLElement>
  listeners?: Record<string, unknown>
}

interface TopicEntryProps {
  item: GetSponsorTopicsResponse[0]
  moduleCount: number
  onDisplayNameChange?: (id: string, newName: string) => void
  showWarning?: boolean
  shouldFocus?: boolean
  showModuleCounts: boolean
  menuProps: TopicEntryMenuProps
  rank?: number
  sortableRef?: (element: HTMLElement | null) => void
  sortableStyle?: CSSProperties
  dragHandle?: TopicEntryDragHandleProps
}

export function TopicEntry({
  item,
  moduleCount,
  onDisplayNameChange,
  showWarning,
  shouldFocus,
  showModuleCounts,
  menuProps,
  rank,
  sortableRef,
  sortableStyle,
  dragHandle,
}: TopicEntryProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [tempName, setTempName] = useState(item.displayName)
  const [validationError, setValidationError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useCheckMobileScreen()

  useEffect(() => {
    setTempName(item.displayName)
  }, [item.displayName])

  useEffect(() => {
    if (shouldFocus) {
      setIsEditing(true)
    }
  }, [shouldFocus])

  useEffect(() => {
    if (isEditing && shouldFocus && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.select()
      }, 0)
    }
  }, [isEditing, shouldFocus])

  const handleDisplayNameChange = (newName: string) => {
    if (onDisplayNameChange && item.id) {
      onDisplayNameChange(item.id, newName)
    }
  }

  const handleEditComplete = () => {
    setValidationError(null)
    setIsEditing(false)

    if (tempName !== item.displayName) {
      handleDisplayNameChange(tempName)
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      setTempName(item.displayName)
      setValidationError(null)
      setIsEditing(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempName(e.target.value)
    if (validationError) {
      setValidationError(null)
    }
  }

  const renderEditInput = () => (
    <Box style={{ width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        value={tempName}
        onChange={handleInputChange}
        onBlur={handleEditComplete}
        onKeyDown={handleInputKeyDown}
        autoFocus
        style={{
          ...INPUT_STYLES,
          borderBottom: validationError ? '2px solid var(--mantine-color-red-6)' : undefined,
        }}
      />
      {validationError && (
        <Text size="xs" c="red" mt={2}>
          {validationError}
        </Text>
      )}
    </Box>
  )

  const renderEntryMenuButton = () => <EntryMenuButton {...menuProps} />

  const renderDragHandle = () =>
    dragHandle ? (
      <Box
        ref={dragHandle.setActivatorNodeRef}
        {...dragHandle.attributes}
        {...dragHandle.listeners}
        aria-label={`Reorder ${item.displayName || 'topic'}`}
        style={{ flexShrink: 0, display: 'flex', alignItems: 'center', cursor: 'grab', touchAction: 'none' }}>
        <IconGripVertical size={16} aria-hidden="true" />
      </Box>
    ) : null

  const renderRank = () =>
    rank !== undefined ? (
      <Text size="sm" fw={500} c="dimmed" ta="center" style={{ flexShrink: 0, minWidth: 20 }}>
        {rank}
      </Text>
    ) : null

  const renderWarningIcon = () =>
    showWarning ? (
      <Tooltip {...WARNING_TOOLTIP_PROPS}>
        <IconAlertCircle size={20} style={{ color: '#fab005', ...(isMobile && { flexShrink: 0 }) }} />
      </Tooltip>
    ) : null

  const renderModuleCount = () =>
    showModuleCounts ? (
      <Text size="sm" c="dimmed" mt={isMobile ? '2px' : undefined}>
        ({moduleCount} modules)
      </Text>
    ) : null

  const renderTitleSection = () => (
    <Box style={{ flex: 1 }}>
      <Text
        size="sm"
        fw={500}
        onClick={() => {
          if (!isEditing) {
            setIsEditing(true)
            setValidationError(null)
          }
        }}
        style={{ cursor: 'pointer' }}
        c={!isEditing && !item.displayName ? 'dimmed' : undefined}>
        {isEditing ? renderEditInput() : item.displayName || 'Add topic name'}
      </Text>
      {isMobile && renderModuleCount()}
    </Box>
  )

  return (
    <Paper ref={sortableRef} style={sortableStyle} p="xs" pl="xs" withBorder radius={0} shadow="0">
      {isMobile ? (
        <Stack gap="sm">
          <Group gap="xs" wrap="nowrap">
            {renderDragHandle()}
            {renderRank()}
            {renderEntryMenuButton()}
            {renderTitleSection()}
            {renderWarningIcon()}
          </Group>
        </Stack>
      ) : (
        <Group wrap="nowrap" align="center">
          {renderDragHandle()}
          {renderRank()}
          {renderEntryMenuButton()}
          <Box style={{ flex: 1 }}>
            {isEditing ? (
              renderEditInput()
            ) : (
              <Group gap="xs" wrap="nowrap">
                <Text
                  size="sm"
                  fw={500}
                  onClick={() => {
                    setIsEditing(true)
                    setValidationError(null)
                  }}
                  style={{ cursor: 'pointer' }}
                  c={!item.displayName ? 'dimmed' : undefined}>
                  {item.displayName || 'Add topic name'}
                </Text>
                <Group gap="xs" wrap="nowrap">
                  {renderModuleCount()}
                  {renderWarningIcon()}
                </Group>
              </Group>
            )}
          </Box>
        </Group>
      )}
    </Paper>
  )
}
